import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Fragment, useState, type ReactNode } from "react";
import { BlueLine } from "../../components/rink/BlueLine";
import { Button } from "../../components/ui/Button";
import { ErrorText } from "../../components/ui/feedback";
import { useI18n } from "../../i18n/I18nProvider";
import { ApiError } from "../../lib/api";
import type { Person, Slot } from "../../lib/api-types";
import { AddPlayers } from "./AddPlayers";
import { SlotRow } from "./SlotRow";
import { useSlotsSave } from "./mutations";
import {
  addDraftSlot,
  draftFromServer,
  hotkeyConflicts,
  isSameDraft,
  moveDraftSlot,
  removeDraftSlot,
  updateDraftSlot,
  type DraftSlot,
} from "./slot-state";

export interface SlotEditorProps {
  workspaceId: string;
  configurationId: string;
  locked: boolean;
  serverSlots: Slot[];
  persons: Person[];
  /** Called after a discard so the parent can remount the editor from server data. */
  onDiscard: () => void;
}

const HOTKEY_CODES = new Set(["hotkey_duplicate", "hotkey_reserved", "hotkey_invalid"]);

/**
 * The lineup: active players above a blue line, bench below. Drag reorders within a group, and the
 * order shown is the playback order that gets saved. The picker adds roster people to the lineup.
 */
export function SlotEditor(props: SlotEditorProps) {
  const { t } = useI18n();
  const { workspaceId, configurationId, locked, persons } = props;
  const livePersons = new Map(persons.map((person) => [person.id, person]));
  const [draft, setDraft] = useState<DraftSlot[]>(() =>
    draftFromServer(props.serverSlots, new Set(livePersons.keys())),
  );
  const save = useSlotsSave(workspaceId, configurationId);
  const serverDraft = draftFromServer(props.serverSlots, new Set(livePersons.keys()));
  const dirty = !isSameDraft(draft, serverDraft);
  const conflicts = hotkeyConflicts(draft);
  const available = persons.filter((person) => !draft.some((slot) => slot.personId === person.id));
  const activeSlots = draft.filter((slot) => slot.active);
  const benchSlots = draft.filter((slot) => !slot.active);
  const lineup = [...activeSlots, ...benchSlots];
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const disabled = locked || save.isPending;
  const hotkeyError = save.error instanceof ApiError && HOTKEY_CODES.has(save.error.code);

  /** Reorders inside one group only: a player moves between the lineup and the bench with the switch. */
  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = lineup.findIndex((slot) => slot.personId === active.id);
    const to = lineup.findIndex((slot) => slot.personId === over.id);
    const source = lineup[from];
    const target = lineup[to];
    if (!source || !target || source.active !== target.active) return;
    setDraft(moveDraftSlot(lineup, from, to));
  };

  const addPerson = (personId: string) => setDraft(addDraftSlot(draft, personId));

  return (
    <section
      className={dirty ? "space-y-4 pb-24 sm:pb-0" : "space-y-4"}
      aria-label={t("tabs.slots")}
    >
      {locked ? null : <AddPlayers available={available} disabled={disabled} onAdd={addPerson} />}

      {draft.length === 0 ? <p className="text-operate text-ink-2">{t("editor.empty")}</p> : null}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext
          items={lineup.map((slot) => slot.personId)}
          strategy={verticalListSortingStrategy}
        >
          <ol className="border-t border-hairline">
            {lineup.map((slot, index) => (
              <Fragment key={slot.personId}>
                {index === activeSlots.length && benchSlots.length > 0 ? <BenchLine /> : null}
                <SlotRow
                  workspaceId={workspaceId}
                  slot={slot}
                  person={livePersons.get(slot.personId)}
                  conflict={conflicts.get(slot.personId)}
                  disabled={disabled}
                  onToggleActive={() =>
                    setDraft(updateDraftSlot(draft, slot.personId, { active: !slot.active }))
                  }
                  onSetHotkey={(hotkey) =>
                    setDraft(updateDraftSlot(draft, slot.personId, { hotkey }))
                  }
                  onRemove={() => setDraft(removeDraftSlot(draft, slot.personId))}
                />
              </Fragment>
            ))}
          </ol>
        </SortableContext>
      </DndContext>

      {dirty ? (
        <div className="rink-bar-rise sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-3 border-t-2 border-ink bg-board px-4 py-3 shadow-board sm:rounded-panel sm:border-t-0">
          <span className="text-note font-semibold text-ink-2" role="status">
            {t("editor.dirty")}
          </span>
          <div className="flex gap-2">
            <Button disabled={disabled} onClick={() => props.onDiscard()}>
              {t("editor.discard")}
            </Button>
            <Button
              variant="primary"
              disabled={disabled || conflicts.size > 0}
              onClick={() => save.mutate(draft)}
            >
              {t("editor.save")}
            </Button>
          </div>
        </div>
      ) : save.isSuccess ? (
        <p className="text-note font-semibold text-ink-2" role="status">
          {t("editor.saved")}
        </p>
      ) : null}
      {save.error ? (
        <div className="space-y-1">
          <p className="text-note font-semibold text-red">{t("editor.slotsErrorTitle")}</p>
          <ErrorText error={save.error} />
          {hotkeyError ? <p className="text-note text-ink-2">{t("editor.hotkeyLocal")}</p> : null}
        </div>
      ) : null}
    </section>
  );
}

/** The blue line between the lineup and the bench, labelled "Bench" at its right end. */
function BenchLine(): ReactNode {
  const { t } = useI18n();
  return (
    <li className="flex items-center gap-3 py-3">
      <BlueLine className="flex-1" />
      <span className="font-display text-lg leading-none font-extrabold uppercase text-blue">
        {t("editor.bench")}
      </span>
    </li>
  );
}
